import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Trophy, User, Target, Lock, LogOut, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ParticipantDashboard = () => {
  const [participant, setParticipant] = useState(null);
  const [players, setPlayers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState({});
  const [tiebreaker, setTiebreaker] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("picks");
  const navigate = useNavigate();

  useEffect(() => {
    const participantId = localStorage.getItem("participant_id");
    if (!participantId) {
      navigate("/");
      return;
    }
    fetchData(participantId);
  }, []);

  const fetchData = async (participantId) => {
    try {
      const [participantRes, playersRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/participants/by-id/${participantId}`),
        axios.get(`${API}/players`),
        axios.get(`${API}/leaderboard`)
      ]);

      setParticipant(participantRes.data);
      setPlayers(playersRes.data);
      setLeaderboard(leaderboardRes.data);

      // Pre-fill selected players if picks exist
      if (participantRes.data.picks && participantRes.data.picks.length > 0) {
        const picks = {};
        participantRes.data.picks.forEach(pick => {
          picks[pick.tier] = pick.player_id;
        });
        setSelectedPlayers(picks);
        setTiebreaker(participantRes.data.tiebreaker || "");
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const handlePlayerSelect = (tier, playerId, playerName) => {
    setSelectedPlayers(prev => ({
      ...prev,
      [tier]: playerId
    }));
  };

  const handleSubmitPicks = async () => {
    if (Object.keys(selectedPlayers).length !== 6) {
      alert("Please select one player from each tier");
      return;
    }

    if (!tiebreaker) {
      alert("Please enter a tiebreaker prediction");
      return;
    }

    setSubmitting(true);
    try {
      const picks = Object.entries(selectedPlayers).map(([tier, playerId]) => {
        const player = players.find(p => p.id === playerId);
        return {
          tier: parseInt(tier),
          player_id: playerId,
          player_name: player.name
        };
      });

      await axios.post(`${API}/participants/${participant.id}/picks`, {
        picks,
        tiebreaker: parseInt(tiebreaker)
      });

      alert("Picks submitted successfully!");
      fetchData(participant.id);
      setActiveTab("leaderboard");
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to submit picks");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("participant_id");
    localStorage.removeItem("participant_name");
    navigate("/");
  };

  const getPlayersByTier = (tier) => {
    return players.filter(p => p.tier === tier);
  };

  const getUserRank = () => {
    const index = leaderboard.findIndex(l => l.name === participant?.name);
    return index >= 0 ? index + 1 : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FDFBF7" }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#006747] mx-auto mb-4"></div>
          <p className="text-[#4A5B52]" style={{ fontFamily: "'Figtree', sans-serif" }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FDFBF7" }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-[#006747]/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#006747] rounded-full flex items-center justify-center">
                <Trophy className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1C2A21]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Masters Pool 2026
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-[#8A9A92]" style={{ fontFamily: "'Figtree', sans-serif" }}>Welcome</p>
                <p className="font-semibold text-[#1C2A21]" style={{ fontFamily: "'Figtree', sans-serif" }}>{participant?.name}</p>
              </div>
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="p-2 hover:bg-[#F3F5F1] rounded-full transition-all"
              >
                <LogOut className="w-5 h-5 text-[#4A5B52]" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Banner */}
        {participant?.picks_locked && (
          <div data-testid="picks-locked-banner" className="mb-6 p-4 bg-[#F2A900]/10 border border-[#F2A900]/30 rounded-lg flex items-center space-x-3">
            <Lock className="w-5 h-5 text-[#F2A900]" strokeWidth={1.5} />
            <p className="text-[#1C2A21] font-medium" style={{ fontFamily: "'Figtree', sans-serif" }}>
              Picks are locked. Tournament has started!
            </p>
          </div>
        )}

        {participant?.picks && participant.picks.length === 6 && !participant?.picks_locked && (
          <div data-testid="picks-submitted-banner" className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600" strokeWidth={1.5} />
            <p className="text-[#1C2A21] font-medium" style={{ fontFamily: "'Figtree', sans-serif" }}>
              Your picks have been submitted! You can still update them before the tournament starts.
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-[#E2E8E4]">
          <div className="flex space-x-8">
            <button
              data-testid="picks-tab"
              onClick={() => setActiveTab("picks")}
              className={`pb-3 px-1 font-semibold transition-all ${
                activeTab === "picks"
                  ? "border-b-2 border-[#006747] text-[#006747]"
                  : "text-[#8A9A92] hover:text-[#4A5B52]"
              }`}
              style={{ fontFamily: "'Figtree', sans-serif" }}
            >
              Make Picks
            </button>
            <button
              data-testid="leaderboard-tab"
              onClick={() => setActiveTab("leaderboard")}
              className={`pb-3 px-1 font-semibold transition-all ${
                activeTab === "leaderboard"
                  ? "border-b-2 border-[#006747] text-[#006747]"
                  : "text-[#8A9A92] hover:text-[#4A5B52]"
              }`}
              style={{ fontFamily: "'Figtree', sans-serif" }}
            >
              Leaderboard
            </button>
          </div>
        </div>

        {/* Make Picks Tab */}
        {activeTab === "picks" && (
          <div data-testid="picks-section">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Select Your Players
              </h2>
              <p className="text-[#4A5B52]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                Choose one player from each tier. The lowest total score wins!
              </p>
            </div>

            <div className="space-y-6">
              {[1, 2, 3, 4, 5, 6].map((tier) => (
                <div key={tier} data-testid={`tier-${tier}-section`} className="bg-white rounded-xl border border-[#E2E8E4] shadow-sm overflow-hidden">
                  <div className="bg-[#004B38] px-6 py-4">
                    <h3 className="text-xl font-bold text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      Tier {tier}
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {getPlayersByTier(tier).map((player) => (
                        <button
                          key={player.id}
                          data-testid={`player-${player.id}-button`}
                          onClick={() => handlePlayerSelect(tier, player.id, player.name)}
                          disabled={participant?.picks_locked}
                          className={`p-4 rounded-lg border-2 transition-all text-left ${
                            selectedPlayers[tier] === player.id
                              ? "border-[#006747] bg-[#006747]/5"
                              : "border-[#E2E8E4] hover:border-[#006747]/50 hover:bg-[#F3F5F1]"
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-semibold text-[#1C2A21]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                                {player.name}
                              </p>
                              <p className="text-sm text-[#8A9A92]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                                {player.odds}
                              </p>
                            </div>
                            {selectedPlayers[tier] === player.id && (
                              <CheckCircle className="w-5 h-5 text-[#006747] flex-shrink-0 ml-2" strokeWidth={2} />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Tiebreaker */}
            <div className="mt-8 bg-white rounded-xl border border-[#E2E8E4] shadow-sm p-6">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-[#F2A900]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Target className="w-5 h-5 text-[#F2A900]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Tiebreaker
                  </h3>
                  <p className="text-[#4A5B52] mb-4" style={{ fontFamily: "'Figtree', sans-serif" }}>
                    Enter your prediction for the winning score (under par)
                  </p>
                  <div className="flex items-center space-x-3">
                    <input
                      data-testid="tiebreaker-input"
                      type="number"
                      value={tiebreaker}
                      onChange={(e) => setTiebreaker(e.target.value)}
                      disabled={participant?.picks_locked}
                      placeholder="e.g., -14"
                      className="w-full sm:w-64 px-4 py-2.5 border border-[#E2E8E4] rounded focus:ring-2 focus:ring-[#006747] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ fontFamily: "'Figtree', sans-serif" }}
                    />
                    <span className="text-[#4A5B52] text-sm" style={{ fontFamily: "'Figtree', sans-serif" }}>under par</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            {!participant?.picks_locked && (
              <div className="mt-8 flex justify-center">
                <button
                  data-testid="submit-picks-button"
                  onClick={handleSubmitPicks}
                  disabled={submitting || Object.keys(selectedPlayers).length !== 6 || !tiebreaker}
                  className="px-8 py-3 bg-[#006747] hover:bg-[#005238] text-white font-semibold rounded transition-all duration-300 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: "'Figtree', sans-serif" }}
                >
                  {submitting ? "Submitting..." : participant?.picks?.length > 0 ? "Update Picks" : "Submit Picks"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === "leaderboard" && (
          <div data-testid="leaderboard-section">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Leaderboard
              </h2>
              <p className="text-[#4A5B52]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                Track the competition standings
              </p>
            </div>

            <div className="bg-white rounded-xl border border-[#E2E8E4] shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#F3F5F1]">
                      <th className="px-6 py-4 text-sm font-semibold text-[#1C2A21] uppercase tracking-wider" style={{ fontFamily: "'Figtree', sans-serif" }}>Rank</th>
                      <th className="px-6 py-4 text-sm font-semibold text-[#1C2A21] uppercase tracking-wider" style={{ fontFamily: "'Figtree', sans-serif" }}>Participant</th>
                      <th className="px-6 py-4 text-sm font-semibold text-[#1C2A21] uppercase tracking-wider" style={{ fontFamily: "'Figtree', sans-serif" }}>Total Score</th>
                      <th className="px-6 py-4 text-sm font-semibold text-[#1C2A21] uppercase tracking-wider" style={{ fontFamily: "'Figtree', sans-serif" }}>Tiebreaker</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((entry, index) => (
                      <tr
                        key={entry.name + index}
                        data-testid={`leaderboard-row-${index}`}
                        className={`border-t border-[#E2E8E4] ${
                          entry.name === participant?.name ? "bg-[#F2A900]/10" : "hover:bg-[#F3F5F1]"
                        }`}
                      >
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                              index === 0 ? "bg-[#F2A900] text-[#1A1F16]" : "bg-[#F3F5F1] text-[#4A5B52]"
                            }`}
                            style={{ fontFamily: "'Figtree', sans-serif" }}
                          >
                            {index + 1}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-semibold text-[#1C2A21]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                            {entry.name}
                            {entry.name === participant?.name && (
                              <span className="ml-2 text-xs text-[#8A9A92]">(You)</span>
                            )}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-[#1C2A21] font-semibold" style={{ fontFamily: "'Figtree', sans-serif" }}>
                            {entry.total_score !== null ? entry.total_score : "-"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-[#4A5B52]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                          {entry.tiebreaker !== null && entry.tiebreaker !== undefined ? `${entry.tiebreaker}` : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ParticipantDashboard;